import { FormProvider, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { useSnackbar } from "../../../../../redux/snackbarProvider";
import { useLayoutEffect, useState } from "react";
import { IUserDTO } from "../../../../../api-services/manage-user-service";
import { cleanUpStepperFormData, setStepperFormData } from "../../../../../redux/slices/stepper-form-slice";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import { IServerErrorResponse } from "../../../../../commons/components/interface";
import { AxiosError } from "axios";
import { getErrorKeyPosition } from "../../../../../commons/utilities/form-utils";
import { AppBorderBox, AppBox, AppGridBox, AppLink, AppText } from "../../../../../commons/components";
import ProfileImageEdit from "./ProfileImageEdit";
import ProfileBasicInfoEdit from "./ProfileBasicInfoEdit";
import { IUser } from "../../../../../commons/models/i-user";
import { profileEdit } from "../../../../../redux/slices/profile-edit-slice";
import { User } from "../../../../../commons/models/user";

interface ProfileEditProps {
  userData: IUser;
  onSave: (data: User) => void;
}

const stepperFormOptions = [
  {
    step: 1,
    title: "Profile Image",
  },
  {
    step: 2,
    title: "Basic Information",
  },
];

const stepperFromKeyMap = [["avatar"], ["firstName", "middleName", "lastName"]];

export default function ProfileEdit({ userData, onSave }: ProfileEditProps) {
  const methods = useForm();
  const formErrors = methods.formState.errors;
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const [selectedStepperFormOption, setSelectedStepperFormOption] = useState(stepperFormOptions[0]);
  const formData = useAppSelector<IUserDTO>((state) => state.stepperForm.stepperFormData);
  const { authUserStatus } = useAppSelector((state) => state.profileEdit);
  const { uploadedImageStatus } = useAppSelector((state) => state.imageUpload);
  const { mobileView } = useAppSelector((state) => state.screenResize);

  //initiate the stepperData
  useLayoutEffect(() => {
    if (userData) {
      const userDTO: IUserDTO = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        middleName: userData.middleName,
        avatar: userData.avatar,
      };
      dispatch(setStepperFormData<IUserDTO>(userDTO));
    }
  }, [userData]);

  const handleNext = (currentStep: number) => {
    if (authUserStatus !== StatusEnum.Loading && uploadedImageStatus !== StatusEnum.Loading) {
      setSelectedStepperFormOption(stepperFormOptions[currentStep]);
    }
  };
  const handleBack = (currentStep: number) => {
    if (authUserStatus !== StatusEnum.Loading && uploadedImageStatus !== StatusEnum.Loading) {
      setSelectedStepperFormOption(stepperFormOptions[currentStep - 2]);
    }
  };

  const handleStepperOptionClick = (option: any) => {
    if (authUserStatus !== StatusEnum.Loading && uploadedImageStatus !== StatusEnum.Loading) {
      setSelectedStepperFormOption(option);
    }
  };

  const handleFormSubmit = () => {
    const formattedValues: IUserDTO = {
      ...formData,
      avatar: formData.avatar ? formData.avatar : null,
    };
    dispatch(profileEdit({ authUserData: formattedValues }))
      .unwrap()
      .then((res) => {
        addSnackbar({
          key: "profile-edit-success",
          text: "User Edited Successfully",
          variant: "success",
        });
        dispatch(cleanUpStepperFormData());
        onSave(res);
      })
      .catch((error: AxiosError<IServerErrorResponse>) => {
        const responseData = error.response?.data;
        if (error.response?.status === 417) {
          addSnackbar({
            key: "error",
            text: "Form not valid",
            variant: "danger",
          });
          if (responseData) {
            const stepperPositionSet = new Set<number>();
            Object.entries(responseData).forEach(([field, messages]) => {
              messages.forEach((message: string) => {
                methods.setError(field, { message });
                const stepperPosition = getErrorKeyPosition(field, stepperFromKeyMap);
                if (stepperPosition) {
                  stepperPositionSet.add(stepperPosition.step);
                }
                const lowestStep = Math.min(...Array.from(stepperPositionSet));
                setSelectedStepperFormOption(stepperFormOptions[lowestStep]);
              });
            });
          }
        } else {
          addSnackbar({
            key: "error",
            text: responseData?.message,
            variant: "danger",
          });
        }
      });
  };

  const clearErrorField = (key: string) => {
    methods.clearErrors(key);
  };

  return (
    <FormProvider {...methods}>
      <AppBox flexDirection="column" gap="sm" style={{ height: mobileView ? "65vh" : "var(--modal-md)" }}>
        <AppGridBox
          className={"flex-1"}
          style={mobileView ? { gridTemplateRows: "auto 1fr", height: "100%" } : { gridTemplateColumns: "1fr 3fr" }}
          gap="sm"
        >
          {!mobileView ? (
            <AppBorderBox border={["Right"]}>
              <AppBox flexDirection="column" gap="2xs" className="w-100">
                {stepperFormOptions.map((option, index) => (
                  <AppLink
                    key={index}
                    onClick={(e) => {
                      e.preventDefault();
                      handleStepperOptionClick(option);
                    }}
                  >
                    <AppBorderBox
                      border={option.step === selectedStepperFormOption.step ? ["Right"] : []}
                      borderColor="primary"
                    >
                      <AppBox pl="sm" py="2xs">
                        <AppText
                          fontWeight="semibold"
                          size="lg"
                          color={option.step === selectedStepperFormOption.step ? "primary" : "currentColor"}
                        >
                          {option.title}
                        </AppText>
                      </AppBox>
                    </AppBorderBox>
                  </AppLink>
                ))}
              </AppBox>
            </AppBorderBox>
          ) : (
            <AppBox displayBlock>
              <AppBorderBox border={["Bottom"]} borderColor="primary">
                <AppBox py="2xs">
                  <AppText fontWeight="semibold" size="lg" color="primary">
                    {selectedStepperFormOption.title}
                  </AppText>
                </AppBox>
              </AppBorderBox>
            </AppBox>
          )}
          <AppBox flexDirection="column" className="w-100 flex-1">
            <AppBox
              className="flex-1"
              style={selectedStepperFormOption.step === 1 ? { display: "flex" } : { display: "none" }}
            >
              <ProfileImageEdit
                userData={userData}
                formStep={selectedStepperFormOption.step}
                formErrors={formErrors}
                onSkipOrNext={handleNext}
                onClearError={clearErrorField}
                submitStatus={authUserStatus}
                onSaveAndExit={handleFormSubmit}
              />
            </AppBox>
            <AppBox
              className="flex-1"
              style={selectedStepperFormOption.step === 2 ? { display: "flex" } : { display: "none" }}
            >
              <ProfileBasicInfoEdit
                userData={userData}
                formStep={selectedStepperFormOption.step}
                submitStatus={authUserStatus}
                formErrors={formErrors}
                onBack={handleBack}
                onSubmit={handleFormSubmit}
                onClearError={clearErrorField}
              />
            </AppBox>
          </AppBox>
        </AppGridBox>
      </AppBox>
    </FormProvider>
  );
}
